import type { ReactNode } from 'react';

import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';
import { EmptyState } from '../../components/ui';
import { useQueryParams, type ParamValue } from '../../lib/query-params';
import { AuditLogTab } from './AuditLogTab';
import { PartnerPoolTab } from './PartnerPoolTab';
import { ReleaseNotesSection } from './ReleaseNotesSection';
import { SnapshotsTab } from './SnapshotsTab';
import { StatusPanel } from './StatusPanel';
import { StatusesTab } from './StatusesTab';
import { UsersTab } from './UsersTab';

import './settings.css';

/**
 * Settings (И8: system status / snapshots admin / audit log / users & roles,
 * tasks T.C5 + T.C4b).
 *
 * Four tabs behind ONE route (`/settings?view=…`): the router is
 * orchestrator-owned (docs/05 §6), so the page owns its own views. T.X2 (owner,
 * 2026-09-11) makes the WHOLE screen admin-only — the router guards `/settings`
 * with `minRole="ADMIN"`, so «Настройки» no longer is the one screen a VIEWER
 * could open, and the `isAdmin` checks below are the second belt on the tabs.
 *
 * Gating therefore happens twice: the tabs are not RENDERED for a non-admin, and
 * a hand-edited `?view=audit` is not silently downgraded to the status panel —
 * it gets the same «Недоступно» the api's 403 means, so a missing tab never
 * reads as "nothing here yet".
 */
export function SettingsPage() {
  const { params, setParams } = useQueryParams();
  const { role } = useAuth();
  const isAdmin = hasAtLeast(role, 'ADMIN');

  const requested = params[PARAM.VIEW] ?? VIEW_STATUS;
  const view: SettingsView = isSettingsView(requested) ? requested : VIEW_STATUS;
  const setView = (next: SettingsView) =>
    setParams({ [PARAM.VIEW]: next === VIEW_STATUS ? undefined : next });

  return (
    <>
      <div className="page-header">
        <h1>Настройки</h1>
        <p className="page-header__sub">
          Состояние системы, журнал снапшотов, аудит и роли пользователей.
        </p>
        <nav className="settings-tabs" aria-label="Разделы настроек" data-testid="settings-tabs">
          <TabButton active={view === VIEW_STATUS} testId="tab-status" onClick={() => setView(VIEW_STATUS)}>
            Статус системы
          </TabButton>
          {isAdmin ? (
            <TabButton
              active={view === VIEW_SNAPSHOTS}
              testId="tab-snapshots"
              onClick={() => setView(VIEW_SNAPSHOTS)}
            >
              Снапшоты
            </TabButton>
          ) : null}
          {isAdmin ? (
            <TabButton active={view === VIEW_AUDIT} testId="tab-audit" onClick={() => setView(VIEW_AUDIT)}>
              Журнал аудита
            </TabButton>
          ) : null}
          {isAdmin ? (
            <TabButton active={view === VIEW_USERS} testId="tab-users" onClick={() => setView(VIEW_USERS)}>
              Пользователи и роли
            </TabButton>
          ) : null}
          {/* Wave 9 (T.Y9) — the manager-status dictionary (D17) and the partner's
              shared online pool (#29, D2), both ADMIN writes of contract v1.8.0. */}
          {isAdmin ? (
            <TabButton active={view === VIEW_STATUSES} testId="tab-statuses" onClick={() => setView(VIEW_STATUSES)}>
              Статусы менеджера
            </TabButton>
          ) : null}
          {isAdmin ? (
            <TabButton
              active={view === VIEW_PARTNER_POOL}
              testId="tab-partner-pool"
              onClick={() => setView(VIEW_PARTNER_POOL)}
            >
              Онлайн-квота партнёра
            </TabButton>
          ) : null}
        </nav>
      </div>

      {view === VIEW_SNAPSHOTS ? (
        isAdmin ? (
          <SnapshotsTab />
        ) : (
          <Forbidden tab="снапшоты" />
        )
      ) : null}
      {view === VIEW_AUDIT ? (
        isAdmin ? (
          <AuditLogTab />
        ) : (
          <Forbidden tab="журнал аудита" />
        )
      ) : null}
      {view === VIEW_USERS ? (
        isAdmin ? (
          <UsersTab />
        ) : (
          <Forbidden tab="пользователи и роли" />
        )
      ) : null}
      {view === VIEW_STATUSES ? (
        isAdmin ? (
          <StatusesTab />
        ) : (
          <Forbidden tab="статусы менеджера" />
        )
      ) : null}
      {view === VIEW_PARTNER_POOL ? (
        isAdmin ? (
          <PartnerPoolTab />
        ) : (
          <Forbidden tab="онлайн-квота партнёра" />
        )
      ) : null}
      {/* T.Z103 — «О релизе» rides the status view: the footer above it shows the
          running version tag, and the newest block's `tag` is that same value. */}
      {view === VIEW_STATUS ? (
        <>
          <StatusPanel />
          <ReleaseNotesSection />
        </>
      ) : null}
    </>
  );
}

/**
 * The refusal a hand-edited URL meets. It must not fetch — the api would answer
 * 403 to this role anyway, and a viewer has no business asking.
 */
function Forbidden({ tab }: { tab: string }) {
  return (
    <div className="card">
      <EmptyState title="Недоступно" hint={`Раздел «${tab}» доступен только администратору.`} />
    </div>
  );
}

function TabButton({
  active,
  testId,
  onClick,
  children,
}: {
  active: boolean;
  testId: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={active ? 'settings-tab settings-tab--active' : 'settings-tab'}
      data-testid={testId}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// The `?view=` vocabulary
// ---------------------------------------------------------------------------

const PARAM = { VIEW: 'view' } as const;
const VIEW_STATUS = 'status';
const VIEW_SNAPSHOTS = 'snapshots';
const VIEW_AUDIT = 'audit';
const VIEW_USERS = 'users';
// ADDITIVE wave 9 (T.Y9) — the manager-status dictionary and the partner pool.
const VIEW_STATUSES = 'statuses';
const VIEW_PARTNER_POOL = 'partner-pool';

type SettingsView =
  | typeof VIEW_STATUS
  | typeof VIEW_SNAPSHOTS
  | typeof VIEW_AUDIT
  | typeof VIEW_USERS
  | typeof VIEW_STATUSES
  | typeof VIEW_PARTNER_POOL;

function isSettingsView(value: ParamValue): value is SettingsView {
  return (
    value === VIEW_SNAPSHOTS ||
    value === VIEW_AUDIT ||
    value === VIEW_USERS ||
    value === VIEW_STATUSES ||
    value === VIEW_PARTNER_POOL
  );
}
